import React, { useRef } from 'react';
import styled, { useTheme } from 'styled-components';
import { CustomPopup } from '../CustomPopup';
import IconButton from '../IconButton';
import { WarningIcon } from 'app/assets/icons';
import { CommonMessages } from 'locales/CommonMessage/messages';
import { useTranslation } from 'react-i18next';
import { TextAreaComponent } from '../TextArea';
import { TextAreaChangeEvent } from '@progress/kendo-react-inputs';
import SimpleReactValidator from 'simple-react-validator';
import { CustomDatepicker } from '../CustomDatepicker';

type Props = {
  isShowPopup?: boolean;
  setIsShowPopup?: React.Dispatch<React.SetStateAction<boolean>>;
  onCancelClick?: () => void;
  onDiscardClickOrDelete?: () => void;
  mainTitle?: string;
  subTitle?: string;
  button1Label?: string;
  button2Label?: string;
  isReasonField?: boolean;
  textAreaLabel?: string;
  onChangeText?: (event: TextAreaChangeEvent) => void;
  isDeleteDisabled?: boolean;
  placeholder?: string;
  className?: string;
  type?: 'closeFacility';
  isDateField?: boolean;
  DatePickerLabel?: string;
  onChangeDateField?: (event: any) => void;
  DatePickerPlaceholder?: string;
  datePickerValue?: any;
  datePickerDisabled?: boolean;
  closureReason?: string;
  isMainLoader?: boolean;
};

export const DiscardPopUp = ({
  isShowPopup,
  setIsShowPopup,
  onCancelClick,
  onDiscardClickOrDelete,
  mainTitle,
  subTitle,
  button1Label,
  button2Label,
  isReasonField,
  textAreaLabel,
  onChangeText,
  isDeleteDisabled,
  placeholder,
  className,
  type,
  isDateField,
  DatePickerLabel,
  onChangeDateField,
  DatePickerPlaceholder,
  datePickerValue,
  datePickerDisabled,
  closureReason,
  isMainLoader,
}: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const simpleValidator = useRef(new SimpleReactValidator());
  const [trigger, setTrigger] = React.useState<boolean>(false);
  return (
    <CustomPopup
      className={`custom-discard-popup ${className} ${
        type === 'closeFacility' ? 'close-facility-popup' : ''
      }`}
      visible={isShowPopup}
      footer
      isMainLoader={isMainLoader}
      footerChildren={
        <Wrapper>
          {isDateField && (
            <CustomDatepicker
              className="custom-datepicker"
              title={DatePickerLabel ? DatePickerLabel : ''}
              onChange={onChangeDateField}
              customPlaceHolder={
                DatePickerPlaceholder ? DatePickerPlaceholder : ''
              }
              validator={isDateField ? simpleValidator.current : undefined}
              validations={isDateField ? 'required' : undefined}
              value={datePickerValue}
              disabled={datePickerDisabled ? datePickerDisabled : false}
              hideStar
            />
          )}
          <ButtonWrapper
            className={`button-wrapper ${
              type === 'closeFacility' ? 'close-facility-popup' : ''
            }`}
          >
            {isReasonField && (
              <TextAreaComponent
                className={'styled-textarea'}
                label={textAreaLabel ? textAreaLabel : ''}
                onChangeText={onChangeText}
                placeholder={placeholder}
                rows={5}
                validator={isReasonField ? simpleValidator.current : undefined}
                validations={isReasonField ? 'required' : undefined}
                hideStar
                value={closureReason}
              />
            )}
            <IconButton
              label={button1Label ? button1Label : t(CommonMessages.discard())}
              borderRadius="4px"
              textColor={theme.white}
              hoverTextColor={theme.white}
              hoverSvgColor={theme.white}
              bgColor={
                type === 'closeFacility' ? theme.black : theme.deleteBtnClr
              }
              borderColor={
                type === 'closeFacility' ? theme.black : theme.deleteBtnClr
              }
              hoverBgColor={
                type === 'closeFacility'
                  ? theme.discardBgHvrClr
                  : theme.deleteBtnHvrClr
              }
              hoverBorderColor={
                type === 'closeFacility'
                  ? theme.discardBgHvrClr
                  : theme.deleteBtnHvrClr
              }
              height="33px"
              fontWeight="600"
              onClick={() => {
                simpleValidator.current.messagesShown = true;
                setTrigger(!trigger);
                onDiscardClickOrDelete?.();
              }}
              disabled={isDeleteDisabled}
              wrapperClassName="discard-button-wrapper"
            />
            <IconButton
              label={button2Label ? button2Label : t(CommonMessages.cancel())}
              bgColor={theme.white}
              borderColor={theme.cancelButtonTxtClr}
              borderRadius="4px"
              textColor={theme.cancelButtonTxtClr}
              hoverBgColor={theme.exportBtnHoverBg}
              hoverTextColor={theme.white}
              hoverBorderColor={theme.exportBtnHoverBg}
              hoverSvgColor={theme.white}
              height="33px"
              fontWeight="600"
              wrapperClassName="cancel-button-wrapper"
              onClick={() => {
                simpleValidator.current.messagesShown = false;
                simpleValidator.current.visibleFields = [];
                setIsShowPopup?.(false);
                onCancelClick?.();
              }}
            />
          </ButtonWrapper>
        </Wrapper>
      }
    >
      <ContentWrapper className={isReasonField ? '' : 'no-reason-field'}>
        <IconWrapper className="icon-wrapper">
          <WarningIcon />
        </IconWrapper>
        <div className="main-title">{mainTitle}</div>
        <div className="reason">
          {subTitle ? (
            subTitle
          ) : (
            <>
              {t(CommonMessages.allUnsavedInformation())}
              <br />
              {t(CommonMessages.willBeLostIfYouGoBackAreYouSure())}
            </>
          )}
        </div>
      </ContentWrapper>
    </CustomPopup>
  );
};
const IconWrapper = styled.div`
  margin-bottom: 30px;
  @media (min-height: 100px) and (max-height: 400px) {
    margin-bottom: 0;
  }
  width: 62px;
  height: 62px;
  border-radius: 50%;
  background: ${p => p.theme.warningPopupIcnBg};
  border: 1px solid ${p => p.theme.warningPopupIconBrdrClr};
  svg {
    margin-top: 14px;
  }
`;
const ButtonWrapper = styled.div`
  .styled-textarea {
    margin-bottom: 26px;
    .label {
      font-size: 11px;
      color: ${p => p.theme.popupTextareaLabel};
    }
    textarea {
      height: 55px;
    }
  }
  button {
    width: 100%;
    font-weight: 700;
    @media (min-width: 300px) and (max-width: 768px) {
      height: 40px;
    }
  }
  .cancel-button-wrapper {
    margin-top: 13px;
  }
  &.close-facility-popup {
    .styled-textarea {
      textarea {
        height: 143px;
        ::placeholder {
          font-size: 11px;
          font-family: 'Open Sans';
          font-weight: 400;
          text-align: left;
          padding-left: unset !important;
          color: ${p => p.theme.textareaPlaceholder};
        }
      }
    }
  }
`;

const ContentWrapper = styled.div`
  padding-top: 35px;
  &.no-reason-field {
    margin-bottom: 26px !important;
  }
  @media (min-height: 100px) and (max-height: 400px) {
    padding-top: 0;
  }
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin: 0 auto;
  font-family: 'Open Sans';
  line-height: 1.5;
  font-weight: 600;
  .main-title {
    font-size: 15px;
    color: ${p => p.theme.warningPopupMainTitleClr};
    font-weight: 700;
  }
  .reason {
    font-size: 13px;
    font-weight: 400;
    color: ${p => p.theme.warningPopupReasonClr};

    @media (min-width: 300px) and (max-width: 768px) {
      font-size: 14px;
    }
  }
  @media (min-width: 300px) and (max-width: 768px) {
    .icon-wrapper {
      display: none;
    }
  }
`;
const Wrapper = styled.div`
  .custom-datepicker {
    .date-picker-title {
      font-size: 11px;
      color: ${p => p.theme.datePickerLabelPopup};
    }
  }
`;
